
"use client";

import React, { useState, useRef, ChangeEvent, KeyboardEvent, ClipboardEvent } from 'react';
import { toEnglishDigits, toPersianDigits } from '@/utils/persian-helpers';

// Define component props for better flexibility
type OtpInputProps = {
    length?: number; // Number of OTP fields
    onComplete?: (otp: string) => void; // Function to execute after OTP is complete
};

const OtpInput: React.FC<OtpInputProps> = ({ length = 6, onComplete }) => {
    // State to hold OTP values in an array
    const [otp, setOtp] = useState<string[]>(new Array(length).fill(''));
    // useRef for direct access to input elements
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    // Handle changes in each input
    const handleChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
        const englishValue = toEnglishDigits(e.target.value);

        // Only accept numbers and move to the next input
        if (/^[0-9]$/.test(englishValue)) {
            const newOtp = [...otp];
            newOtp[index] = englishValue;
            setOtp(newOtp);

            // If not the last input, move focus to the next one
            if (index < length - 1 && inputRefs.current[index + 1]) {
                inputRefs.current[index + 1]?.focus();
            }

            // If all fields are filled, call the onComplete function
            const newOtpString = newOtp.join('');
            if (newOtpString.length === length) {
                onComplete?.(newOtpString);
            }
        }
    };

    // Handle key presses (especially Backspace)
    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === 'Backspace') {
            const newOtp = [...otp];
            // If the current input is empty, move to the previous input
            if (newOtp[index] === '' && index > 0) {
                 inputRefs.current[index - 1]?.focus();
                 return;
            }
            // Clear the current input's value
             newOtp[index] = '';
             setOtp(newOtp);
        }
    };

    // Handle pasting
    const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pasteData = toEnglishDigits(e.clipboardData.getData('text'))
            .trim()
            .slice(0, length)
            .split('');
        
        if (pasteData.every(char => /^[0-9]$/.test(char))) {
            const newOtp = [...otp];
            let nextFocusIndex = 0;
            pasteData.forEach((char, index) => {
                if(index < length) {
                    newOtp[index] = char;
                    nextFocusIndex = index;
                }
            });
            setOtp(newOtp);

            // Focus on the last filled element or the next one
            if(nextFocusIndex < length - 1) {
                inputRefs.current[nextFocusIndex + 1]?.focus();
            } else {
                inputRefs.current[nextFocusIndex]?.focus();
            }
            
            const newOtpString = newOtp.join('');
            if (newOtpString.length === length) {
                onComplete?.(newOtpString);
            }
        }
    };

    return (
        <div className="flex justify-center items-center space-x-2" dir="ltr">
            {otp.map((value, index) => (
                <input
                    key={index}
                    ref={(el) => {
                        inputRefs.current[index] = el;
                    }}
                    type="text"
                    inputMode="numeric" // Numeric keyboard on mobile
                    maxLength={1}
                    value={toPersianDigits(value)}
                    onChange={(e) => handleChange(e, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onPaste={handlePaste} 
                    className={`
                        w-14 h-16 sm:w-16 sm:h-20
                        border-2 rounded-lg 
                        text-center text-2xl sm:text-3xl font-bold 
                        transition-all duration-300 ease-in-out
                        bg-gray-100 dark:bg-gray-800 mx-1 
                        text-gray-900 dark:text-gray-100
                        ${
                            value
                                ? 'border-indigo-500'
                                : 'border-gray-300 dark:border-gray-600'
                        }
                        focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-400 dark:focus:ring-indigo-600
                        caret-indigo-500
                    `}
                />
            ))}
        </div>
    );
};

export default OtpInput;
